import { Box } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { deleteCollection as deleteCollectionAction } from '../../../actions/collections.actions';
import { boxShadows, palette, spaces } from '../../../assets/styles/themes/tokens';
import { routes } from '../../../constants';
import { CollectionFormModalContext } from '../../../state/CollectionFormModalProvider';
import { NotificationContext } from '../../../state/NotificationContextProvider';
import type { CollectionProperties } from '../../../types/AppContent';
import useAction from '../../../utils/useAction';
import useRouter from '../../../utils/useRouter';
import useScreenSize from '../../../utils/useScreenSize';
import CustomCollectionCard from '../../cards/MuiCard/CustomMuiCard';
import type { MenuItemProps } from '../../cards/MuiCard/CustomMuiCard/CustomMuiCardActions/CustomMuiCardActions';
import ConfirmationDialog from '../../Dialog/ConfirmationDialog';
import CollectionContent from './CollectionContent';

interface MyCollectionsListProps {
	collections: CollectionProperties[];
}

const MyCollectionsList: React.FC<MyCollectionsListProps> = (props) => {
	const { t } = useTranslation();
	const { router } = useRouter();
	const { isXsScreen, isSmScreen, isMdScreen } = useScreenSize();
	const { collections } = props;
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
	const [activeCollection, setActiveCollection] = useState<CollectionProperties | null>(null);
	const deleteCollection = useAction(deleteCollectionAction);
	const { showNotification, hideNotification } = useContext(NotificationContext);
	const { show: openCollectionFormModal } = useContext(CollectionFormModalContext);

	const openDeleteDialog = (collection: CollectionProperties) => {
		setActiveCollection(collection);
		setIsDeleteDialogOpen(true);
	};

	const openEditDialog = (collection: CollectionProperties) => {
		hideNotification(); // hide notifications just in case one is open
		openCollectionFormModal(collection.id, collection);
	};

	const onConfirmAndClose = () => {
		if (activeCollection) {
			deleteCollection(activeCollection.id);
			showNotification({
				message: t('The collection {{name}} has been successfully deleted', {
					name: activeCollection.title,
				}),
				severity: 'success',
			});
			setActiveCollection(null);
		}
		setIsDeleteDialogOpen(false);
	};

	const onCloseDialog = () => {
		setIsDeleteDialogOpen(false);
		setActiveCollection(null);
	};

	const getGridTemplateColumns = () => {
		if (isXsScreen) return 1;
		if (isSmScreen || isMdScreen) return 2;
		return 3;
	};

	const openCollection = (collectionId: string) =>
		router.push(`/${routes.collections.root}/${collectionId}`);

	const getMenuItems = (collection: CollectionProperties): MenuItemProps[] => [
		{
			label: t('Edit'),
			iconName: 'editCollection',
			onClick: () => openEditDialog(collection),
			color: palette.text.primary,
		},
		{
			label: t('Remove'),
			iconName: 'trash',
			onClick: () => openDeleteDialog(collection),
			color: palette.error.main,
		},
	];

	return (
		<Box
			sx={{
				display: 'grid',
				rowGap: spaces.md,
				columnGap: spaces.md,
				flexShrink: 0,
				alignSelf: 'stretch',
				gridTemplateColumns: `repeat(${getGridTemplateColumns()}, minmax(0, 1fr))`,
				gridAutoFlow: 'row',
			}}
		>
			{collections.map((collection) => (
				<CustomCollectionCard
					key={collection.id}
					avatar="collectionsFolder"
					title={collection.title}
					subheader={`${collection.contentsCount} ${collection.contentsCount === 1 ? t('Content') : t('Contents')}`}
					onClick={() => openCollection(collection.id)}
					menuItems={getMenuItems(collection)}
					color={collection.color}
					sxCardStyle={{
						main: {
							cursor: 'pointer',
							'&:focus-visible': {
								outline: 'none',
								borderColor: palette.primary.focusVisible,
								boxShadow: boxShadows.elevation2,
								'& .MuiCardHeader-title': {
									color: palette.primary.main,
								},
							},
							'&:hover': {
								boxShadow: boxShadows.elevation2,
								'& .MuiCardHeader-title': {
									color: palette.primary.main,
								},
							},
						},
					}}
				>
					<CollectionContent collection={collection} />
				</CustomCollectionCard>
			))}
			<ConfirmationDialog
				isOpen={isDeleteDialogOpen}
				title={t('Delete Collection')}
				message={t('Are you sure that you want to delete the collection')}
				onConfirm={onConfirmAndClose}
				onCloseModal={onCloseDialog}
				confirmButtonText={t('Yes, remove')}
				confirmButtonColor="error"
			/>
		</Box>
	);
};

export default React.memo(MyCollectionsList);
